import API from "utils/API";
import authHeader from "utils/auth-header";
import { IProject } from "../domain/IProject";
import { IGetAllProjectsResponse } from "../domain/IGetAllProjectsResponse";
import { IProjectFinancials } from "../domain/IProjectFinancials";
import { ICreateProjectRequestParams } from "../domain/ICreateProjectRequestParams";
import { IGetProjectsListRequestParams } from "../domain/IGetProjectsListRequestParams";
import { IMessage } from "domain/IMessage";
import { IUpdateProjectRequestParams } from "../domain/IUpdateProjectRequestParams";
import { IType } from "Dashboard/domain/IType";
import { ICategory } from "Dashboard/domain/ICategory";
import { getQeuryParams } from "utils/queryParamsUtil";
import { IGetFinancialsRequestParams } from "../domain/IGetFinancialsRequestParams";
import { IResponseFinishedProjectsSimple } from "../domain/IResponseFinishedProjectsSimple";
import { IProjectSimple } from "Dashboard/domain/IProjectSimple";
import { IConsumableExemptionFinancials } from "../domain/IConsumableExemptionFinancials";
import { IConcreteBaseFinancials } from "../domain/IConcreteBaseFinancials";
import { IAccountPayableFinancials } from "../domain/IAccountPayableFinancials";
import { IAccountReceivableFinancials } from "../domain/IAccountReceivableFinancials";
import { ICarnetsFinancials } from "../domain/ICarnetsFinancials";
import { IStockOutputFinancials } from "../domain/IStockOutputFinancials";

const getProjectsAdminList = async (
  data?: IGetProjectsListRequestParams
): Promise<IProject[]> => {
  var headers = authHeader();
  try {
    const response = await API.get<IGetAllProjectsResponse>(
      `/projects-admin/protected/project${getQeuryParams(data)}`,
      { headers }
    );
    let projects = response.data.projects;
    return projects;
  } catch (err) {
    throw err;
  }
};

const deleteProject = async (projectId: number): Promise<IMessage> => {
  var headers = authHeader();
  try {
    const response = await API.delete<IMessage>(
      `/projects-admin/protected/project/${projectId}`,
      { headers }
    );
    let message = response.data;
    return message;
  } catch (err) {
    throw err;
  }
};

const getProject = async (projectId: number): Promise<IProject> => {
  var headers = authHeader();
  try {
    const response = await API.get<IProject>(
      `/projects-basic/public/project/${projectId}`,
      { headers }
    );
    let project = response.data;
    return project;
  } catch (err) {
    throw err;
  }
};

const getProjectsBasicList = async (
  data?: IGetProjectsListRequestParams
): Promise<IProject[]> => {
  var headers = authHeader();
  try {
    const response = await API.get<IGetAllProjectsResponse>(
      `/projects-basic/protected/project${getQeuryParams(data)}`,
      { headers }
    );
    let projects = response.data.projects;
    return projects;
  } catch (err) {
    throw err;
  }
};

const createProject = async (
  data: ICreateProjectRequestParams
): Promise<IProject> => {
  var headers = authHeader();
  try {
    const response = await API.post<IProject>(
      "/projects-admin/protected/project",
      data,
      { headers }
    );
    let project = response.data;
    return project;
  } catch (err) {
    throw err;
  }
};

const getProjectFinancials = async (
  data: IGetFinancialsRequestParams,
  signal: AbortSignal
): Promise<IProjectFinancials> => {
  var headers = authHeader();
  try {
    const { projectId, ...otherData } = data;;
    const response = await API.get<IProjectFinancials>(
      `/projects-basic/public/project/${data.projectId}/financials${getQeuryParams(otherData)}`,
      { headers, signal }
    );
    let financials = response.data;
    return financials;
  } catch (err) {
    throw err;
  }
};

const getAccountPayableFinancials = async (
  data: IGetFinancialsRequestParams,
  signal: AbortSignal
): Promise<IAccountPayableFinancials> => {
  var headers = authHeader();
  try {
    const { projectId, ...otherData } = data;
    const response = await API.get<IAccountPayableFinancials>(
      `/projects-basic/public/project/${data.projectId}/financials/payables${getQeuryParams(otherData)}`,
      { headers, signal }
    );
    let financials = response.data;
    return financials;
  } catch (err) {
    throw err;
  }
};

const getAccountReceivableFinancials = async (
  data: IGetFinancialsRequestParams,
  signal: AbortSignal
): Promise<IAccountReceivableFinancials> => {
  var headers = authHeader();
  try {
    const { projectId, ...otherData } = data;
    const response = await API.get<IAccountReceivableFinancials>(
      `/projects-basic/public/project/${data.projectId}/financials/receivables${getQeuryParams(otherData)}`,
      { headers, signal }
    );
    let financials = response.data;
    return financials;
  } catch (err) {
    throw err;
  }
};

const getCarnetFinancials = async (
  data: IGetFinancialsRequestParams,
  signal: AbortSignal
): Promise<ICarnetsFinancials> => {
  var headers = authHeader();
  try {
    const { projectId, ...otherData } = data;
    const response = await API.get<ICarnetsFinancials>(
      `/projects-basic/public/project/${data.projectId}/financials/carnets${getQeuryParams(otherData)}`,
      { headers, signal }
    );
    let financials = response.data;
    return financials;
  } catch (err) {
    throw err;
  }
};

const getStockOutputFinancials = async (
  data: IGetFinancialsRequestParams,
  signal: AbortSignal
): Promise<IStockOutputFinancials> => {
  var headers = authHeader();
  try {
    const { projectId, ...otherData } = data;
    const response = await API.get<IStockOutputFinancials>(
      `/projects-basic/public/project/${data.projectId}/financials/stock-output${getQeuryParams(otherData)}`,
      { headers, signal }
    );
    let financials = response.data;
    return financials;
  } catch (err) {
    throw err;
  }
};

const getProjectConsumableFinancials = async (
  data: IGetFinancialsRequestParams,
  signal: AbortSignal
): Promise<IConsumableExemptionFinancials> => {
  var headers = authHeader();
  try {
    const { projectId, ...otherData } = data;
    const response = await API.get<IConsumableExemptionFinancials>(
      `/projects-basic/public/project/${projectId}/financials/consumables${getQeuryParams(otherData)}`,
      { headers, signal }
    );
    let consumableExemptionFinancials = response.data;
    return consumableExemptionFinancials;
  } catch (err) {
    throw err;
  }
};

const getProjectConcreteBaseFinancials = async (
  data: IGetFinancialsRequestParams,
  signal: AbortSignal
): Promise<IConcreteBaseFinancials> => {
  var headers = authHeader();
  try {
    const { projectId, ...otherData } = data;
    const response = await API.get<IConcreteBaseFinancials>(
      `/projects-basic/public/project/${projectId}/financials/concrete-base${getQeuryParams(otherData)}`,
      { headers, signal }
    );
    let concreteBaseFinancials = response.data;
    return concreteBaseFinancials;
  } catch (err) {
    throw err;
  }
};

export interface GetProjectsForDropDownOptions {
  showHiddenInDropDowns: boolean;
};
const getProjectsForPayable = async (options: GetProjectsForDropDownOptions): Promise<IProjectSimple[]> => {
  var headers = authHeader();
  try {
    var url;
    if (options.showHiddenInDropDowns) {
      url = `/projects-basic/public/projects-for-payable?showHiddenInDropDowns=true`;
    } else {
      url = "/projects-basic/public/projects-for-payable?showHiddenInDropDowns=false";
    }
    const response = await API.get<IResponseFinishedProjectsSimple>(url, { headers });
    let projects = response.data.projects;
    return projects;
  } catch (err) {
    throw err;
  }
};

const getProjectsForReceivable = async (partnerId: number): Promise<IProjectSimple[]> => {
  var headers = authHeader();
  try {
    const response = await API.get<IResponseFinishedProjectsSimple>(
      `/projects-basic/public/projects-for-receivable${getQeuryParams({ partnerId })}`,
      { headers }
    );
    let projects = response.data.projects;
    return projects;
  } catch (err) {
    throw err;
  }
};

const getTypes = async (): Promise<IType[]> => {
  var headers = authHeader();
  try {
    const response = await API.get<IType[]>(`/projects-basic/public/project-type`, {
      headers,
    });
    let types = response.data;
    return types;
  } catch (err) {
    throw err;
  }
};

const getCategories = async (): Promise<ICategory[]> => {
  var headers = authHeader();
  try {
    const response = await API.get<ICategory[]>(
      `/projects-basic/public/project-category`,
      { headers }
    );
    let types = response.data;
    return types;
  } catch (err) {
    throw err;
  }
};

const createType = async (name: String): Promise<IType> => {
  var headers = authHeader();
  try {
    const response = await API.post<IType>(
      "/projects-admin/protected/project-type",
      { name },
      { headers }
    );
    let project = response.data;
    return project;
  } catch (err) {
    throw err;
  }
};

const updateProject = async (
  data: IUpdateProjectRequestParams
): Promise<IProject> => {
  var headers = authHeader();
  const { projectId, ...rest } = data;
  try {
    const response = await API.put<IProject>(
      `/projects-admin/protected/project/${projectId}`,
      { ...rest },
      { headers }
    );
    let project = response.data;
    return project;
  } catch (err) {
    throw err;
  }
};

export const projectService = {
  getProjectsAdminList,
  deleteProject,
  getProject,
  getProjectsBasicList,
  createProject,
  getProjectFinancials,
  getAccountPayableFinancials,
  getAccountReceivableFinancials,
  getCarnetFinancials,
  getStockOutputFinancials,
  getProjectConsumableFinancials,
  getProjectConcreteBaseFinancials,
  getProjectsForPayable,
  getProjectsForReceivable,
  getTypes,
  getCategories,
  createType,
  updateProject,
};
